/**
 * Month-granular coverage math for the driver application.
 * Mirror of trucking-server/src/utils/applicationCoverage.js. Keep the two in sync.
 */

const MONTH_INPUT_REGEX = /^(0[1-9]|1[0-2])\/(\d{4})$/;
const ISO_MONTH_REGEX = /^(\d{4})-(0[1-9]|1[0-2])$/;

export const parseMonth = (value) => {
  if (!value) return null;
  const str = String(value).trim();
  let match = str.match(MONTH_INPUT_REGEX);
  if (match) return { year: Number(match[2]), month: Number(match[1]) };
  match = str.match(ISO_MONTH_REGEX);
  if (match) return { year: Number(match[1]), month: Number(match[2]) };
  const full = str.match(/^(\d{4})-(\d{2})-(\d{2})$/) || null;
  if (full) return { year: Number(full[1]), month: Number(full[2]) };
  const us = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/) || null;
  if (us) return { year: Number(us[3]), month: Number(us[1]) };
  return null;
};

const toIndex = (ym) => (ym ? ym.year * 12 + (ym.month - 1) : null);
const fromIndex = (index) => ({ year: Math.floor(index / 12), month: (index % 12) + 1 });

export const formatMonth = (index) => {
  const { year, month } = fromIndex(index);
  return `${String(month).padStart(2, '0')}/${year}`;
};

export const currentMonthIndex = (now = new Date()) => now.getFullYear() * 12 + now.getMonth();

export const normalizeRanges = (entries = [], now = new Date()) => {
  const nowIndex = currentMonthIndex(now);
  const ranges = [];
  for (const entry of entries) {
    if (!entry) continue;
    const start = toIndex(parseMonth(entry.from));
    if (start === null) continue;
    let end;
    if (entry.isPresent || !entry.to) {
      end = nowIndex;
    } else {
      end = toIndex(parseMonth(entry.to));
      if (end === null) continue;
    }
    if (end < start) continue;
    ranges.push({ start, end: Math.min(end, nowIndex) });
  }
  return ranges.sort((a, b) => a.start - b.start);
};

export const computeCoverage = ({ entries = [], requiredMonths = 36, now = new Date(), allowedGapMonths = 1 }) => {
  const endIndex = currentMonthIndex(now);
  const startIndex = endIndex - requiredMonths + 1;
  const ranges = normalizeRanges(entries, now);

  const covered = new Array(requiredMonths).fill(false);
  for (const range of ranges) {
    const from = Math.max(range.start, startIndex);
    const to = Math.min(range.end, endIndex);
    for (let i = from; i <= to; i += 1) covered[i - startIndex] = true;
  }

  const gaps = [];
  let gapStart = null;
  for (let i = 0; i <= requiredMonths; i += 1) {
    const isCovered = i < requiredMonths ? covered[i] : true;
    if (!isCovered && gapStart === null) {
      gapStart = i;
    } else if (isCovered && gapStart !== null) {
      gaps.push({ from: formatMonth(startIndex + gapStart), to: formatMonth(startIndex + i - 1), months: i - gapStart });
      gapStart = null;
    }
  }

  const coveredMonths = covered.filter(Boolean).length;
  return {
    requiredStart: formatMonth(startIndex),
    requiredEnd: formatMonth(endIndex),
    requiredMonths,
    coveredMonths,
    missingMonths: requiredMonths - coveredMonths,
    coverageRatio: coveredMonths / requiredMonths,
    gaps,
    gapsNeedingExplanation: gaps.filter((gap) => gap.months > allowedGapMonths),
    isFullyCovered: coveredMonths === requiredMonths
  };
};

export const validateMonthRange = ({ from, to, isPresent }, now = new Date()) => {
  const start = parseMonth(from);
  if (!start) return 'Start month must be in MM/YYYY format';
  const startIndex = toIndex(start);
  const nowIndex = currentMonthIndex(now);
  if (startIndex > nowIndex) return 'Start month cannot be in the future';
  if (startIndex < (1900 * 12)) return 'Start month is too far in the past';
  if (isPresent) return null;
  const end = parseMonth(to);
  if (!end) return 'End month must be in MM/YYYY format, or mark it as Present';
  const endIndex = toIndex(end);
  if (endIndex > nowIndex) return 'End month cannot be in the future';
  if (endIndex < startIndex) return 'End month must be after the start month';
  return null;
};
